import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Divider,
  Grid,
  Skeleton
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { initiatePayment, getPaymentProcessorInfo, formatPrice } from '../services/bookingService';
import { parseISO, format } from 'date-fns';

const PaymentForm = ({
  clinicId,
  serviceId,
  practitionerId,
  amount,
  patientEmail,
  patientName,
  appointmentDetails,
  onSuccess,
  onCancel,
  returnUrl,
  loading: initialLoading = false
}) => {
  const [loading, setLoading] = useState(initialLoading);
  const [paymentUrl, setPaymentUrl] = useState(null);
  const [error, setError] = useState(null);
  const [paymentProcessor, setPaymentProcessor] = useState(null);
  const [processorInfo, setProcessorInfo] = useState(null);
  
  // Check if we should redirect the user to a payment page
  const initiatePaymentProcess = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Extract the clinic stub from the URL
      const pathParts = window.location.pathname.split('/');
      const clinicStub = pathParts[1]; // The first segment after the domain
      
      const paymentData = {
        clinic_id: clinicId,
        clinic_stub: clinicStub, // Include clinic stub for redirect
        appointment_type_id: serviceId,
        practitioner_id: practitionerId,
        amount: amount,
        patient_email: patientEmail,
        patient_name: patientName,
        returnUrl: returnUrl,
        // Include appointment details for auto-creation
        ...appointmentDetails
      };
      
      console.log('Initiating payment with data:', paymentData);
      
      const response = await initiatePayment(paymentData);
      
      setPaymentProcessor(response.processor || 'stripe');
      setPaymentUrl(response.checkout_url);
      
      // Get information about the payment processor
      const info = getPaymentProcessorInfo(response.processor || 'stripe');
      setProcessorInfo(info);
      
      console.log('Payment initiated:', response);
      
      // If the payment requires redirect, we don't set loading to false
      // because we want to show waiting state until the user is redirected
    } catch (err) {
      console.error('Error initiating payment:', err);
      setError(err.message || 'Failed to initiate payment process');
      setLoading(false);
    }
  };
  
  // Automatically initiate payment when component mounts
  useEffect(() => {
    if (!paymentUrl) {
      // Save clinic ID and path info to localStorage for use after redirect
      try {
        // Extract the clinic stub from the URL
        const pathParts = window.location.pathname.split('/');
        const clinicStub = pathParts[1]; // The first segment after the domain
        
        // Save minimal booking info to localStorage
        localStorage.setItem('currentBooking', JSON.stringify({
          clinicId: clinicId,
          clinicStub: clinicStub,
          serviceId: serviceId,
          practitionerId: practitionerId
        }));
        console.log('Saved current booking info to localStorage with clinicStub:', clinicStub);
        
        // Get the full booking data from the parent component
        const bookingSessionData = window.bookingData || {};
        if (Object.keys(bookingSessionData).length === 0) {
          // Try to extract booking data from appointmentDetails
          const bookingData = {
            service: { id: serviceId },
            practitioner: { id: practitionerId },
            appointmentTime: appointmentDetails?.starts_at ? {
              iso: appointmentDetails.starts_at
            } : null,
            patient: {
              first_name: appointmentDetails?.patient_first_name || '',
              last_name: appointmentDetails?.patient_last_name || '',
              email: patientEmail || '',
              phone: appointmentDetails?.patient_phone || ''
            },
            notes: appointmentDetails?.notes || '',
            payment: {
              status: 'pending'
            }
          };
          
          // Save to sessionStorage
          console.log('Saving constructed booking data to sessionStorage');
          sessionStorage.setItem('bookingSession', JSON.stringify(bookingData));
        } else {
          console.log('Saving full booking data to sessionStorage');
          sessionStorage.setItem('bookingSession', JSON.stringify(bookingSessionData));
        }
      } catch (e) {
        console.error('Error saving to storage:', e);
      }
      
      // Initiate payment process
      initiatePaymentProcess();
    }
  }, []);
  
  // Handle redirect to payment processor
  useEffect(() => {
    if (paymentUrl) {
      // Short delay before redirecting to ensure UI updates
      const timer = setTimeout(() => {
        window.location.href = paymentUrl;
      }, 1500);
      
      return () => clearTimeout(timer);
    }
  }, [paymentUrl]);
  
  // Handle successful payment
  const handlePaymentSuccess = (transactionId, accessCode) => {
    if (onSuccess) {
      onSuccess({
        status: 'complete',
        transactionId,
        accessCode
      });
    }
  };
  
  // Parse appointment details for display
  const formatAppointmentTime = () => {
    if (!appointmentDetails || !appointmentDetails.starts_at) return 'Unknown time';
    
    const startTime = parseISO(appointmentDetails.starts_at);
    // TIMEZONE FIX: Always format times in Melbourne timezone
    return new Intl.DateTimeFormat('en-AU', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Australia/Melbourne'
    }).format(startTime);
  };
  
  // Render payment summary
  const renderPaymentSummary = () => {
    return (
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Appointment Summary
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">
                Date & Time
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Typography variant="body2">
                {formatAppointmentTime()}
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">
                Patient
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Typography variant="body2">
                {patientName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {patientEmail}
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">
                Total
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Typography variant="h6" color="primary">
                {formatPrice(amount)}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };
  
  // Main render
  if (loading) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Button 
            startIcon={<ArrowBackIcon />}
            onClick={onCancel}
            sx={{ mr: 2 }}
            disabled={paymentUrl !== null}
          >
            Back
          </Button>
          <Typography variant="h6">
            Payment
          </Typography>
        </Box>
        
        {renderPaymentSummary()}
        
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
          {paymentUrl ? (
            <Box>
              <Typography variant="h6" gutterBottom>
                Redirecting to {processorInfo?.name || 'payment page'}...
              </Typography>
              {processorInfo?.logo && (
                <Box sx={{ mt: 2, mb: 3 }}>
                  <img 
                    src={processorInfo.logo} 
                    alt={`${processorInfo.name} logo`}
                    style={{ maxHeight: 40 }} 
                  />
                </Box>
              )}
              <CircularProgress sx={{ mt: 2 }} />
              <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
                Please do not close this window
              </Typography>
            </Box>
          ) : (
            <Box>
              <Typography variant="h6" gutterBottom>
                Preparing Payment...
              </Typography>
              <CircularProgress sx={{ mt: 2 }} />
            </Box>
          )}
        </Paper>
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Button 
            startIcon={<ArrowBackIcon />}
            onClick={onCancel}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h6">
            Payment
          </Typography>
        </Box>
        
        {renderPaymentSummary()}
        
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button 
            variant="outlined"
            onClick={onCancel}
          >
            Back
          </Button>
          <Button 
            variant="contained"
            onClick={initiatePaymentProcess}
          >
            Try Again
          </Button>
        </Box>
      </Box>
    );
  }
  
  // This should not happen, but just in case
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Payment Processing
      </Typography>
      <CircularProgress />
    </Box>
  );
};

export default PaymentForm;